import { useCallback, useMemo, useState } from 'react'

import { useSelector } from 'react-redux'
import { Modal } from 'antd'
import { isFunction, values } from 'lodash'

import { selectTranslate } from '../../../../reducers/LocaleReducer/LocalReducer'
import { SchoolModalContext } from '../../SchoolModal'
import { findSimilarSchools, School, SchoolValidators } from '../../../../utils/entities/school'
import { isValid } from '../../../../utils/validators'
import { isObject } from '../../../../utils'
import SchoolValidator from '../../../../Components/shared/Schools/SchoolValidator'
import SchoolForm from './SchoolForm'

/** @enum {string} */
export const Steps = Object.freeze({
  FORM: 'FORM',
  VALIDATION: 'VALIDATION'
})

const DEFAULT_HANDLER = Object.freeze({ data: null, callback: null, step: Steps.FORM })

const LABELS = Object.freeze({
  title: 'school_validator.title',
  description: 'school_validator.description',
  duplicate: 'school_validator.duplicate_confirmation',
  merge: 'school_validator.merge_button',
  validate: 'school_validator.create_anyway'
})

const PortalSchoolModal = ({ children, countries = [], schools = [] }) => {
  const t = useSelector(selectTranslate)

  const [formHandler, setFormHandler] = useState(DEFAULT_HANDLER)
  const [errorFields, setErrorFields] = useState([])

  const close = useCallback(() => {
    setFormHandler(DEFAULT_HANDLER)
    setErrorFields([])
  }, [])

  const accept = useCallback(school => {
    if (isFunction(formHandler.callback)) {
      formHandler.callback(school)
    }

    close()
  }, [formHandler.callback, close])

  const handleChange = useCallback(school => setFormHandler(handler => ({ ...handler, data: school })), [])

  const handleSubmit = useCallback(() => {
    const school = new School(formHandler.data).trimmed()
    const errors = isValid(school, values(SchoolValidators))

    setErrorFields(errors.keys)

    if (errors.keys.length > 0) {
      return
    }

    const similar = findSimilarSchools(school, schools)

    if (similar.length > 0) {
      setFormHandler(handler => ({ ...handler, data: new School(school), step: Steps.VALIDATION }))
    } else {
      accept(new School(school))
    }
  }, [formHandler.data, schools, accept])

  const isDataLoaded = isObject(formHandler.data)
  const isValidationStep = formHandler.step === Steps.VALIDATION

  const context = useMemo(() => ({ formHandler, setFormHandler }), [formHandler])

  return (
    <>
      <SchoolModalContext.Provider value={context}>
        {children}
      </SchoolModalContext.Provider>
      <Modal
        visible={isDataLoaded}
        width={isValidationStep ? 700 : 540}
        title={t(isValidationStep ? 'school_validator.modal_title' : 'internship_request_portal.school.create_title')}
        okText={t('modal.ok_button.create')}
        cancelText={t('modal.cancel_button')}
        footer={isValidationStep ? null : undefined}
        onCancel={close}
        onOk={handleSubmit}
      >
        {isDataLoaded && !isValidationStep && (
          <SchoolForm
            countries={countries}
            data={formHandler.data}
            displayTitle={false}
            errorFields={errorFields}
            onChange={handleChange}
          />
        )}
        {isDataLoaded && isValidationStep && (
          <SchoolValidator
            countries={countries}
            labels={LABELS}
            school={formHandler.data}
            schools={schools}
            onMerge={accept}
            onValidate={accept}
          />
        )}
      </Modal>
    </>
  )
}

export default PortalSchoolModal
